import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { needsPlanSelection } from '@/lib/planGate';
import { needsName } from '@/lib/nameGate';

const LOADING_STALL_MS = 30_000;

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * When false, the route only requires authentication — the plan-selection and
   * team-invite onboarding gates are skipped. Used for account-level surfaces
   * (settings, billing) that a plan-less user must still be able to reach.
   */
  requirePlan?: boolean;
}

export function ProtectedRoute({ children, requirePlan = true }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, onboardingProgress, subscription, sessionExpired, hasRole, profile } = useAuth();
  const location = useLocation();
  const [stalled, setStalled] = useState(false);

  // Treat as still loading only while auth is hydrating. Once auth resolves, missing
  // onboarding rows shouldn't trap the user in a perpetual spinner — redirect below.
  const isStillLoading = isLoading;

  // Diagnostic logging for stuck loading states
  useEffect(() => {
    console.log('[ProtectedRoute] isLoading:', isLoading, 'onboardingProgress:', !!onboardingProgress, 'isAuthenticated:', isAuthenticated, 'sessionExpired:', sessionExpired);
  }, [isLoading, onboardingProgress, isAuthenticated, sessionExpired]);

  // Non-destructive fallback: after 30s of loading, show a refresh prompt
  useEffect(() => {
    if (!isStillLoading) {
      setStalled(false);
      return;
    }

    const timer = setTimeout(() => setStalled(true), LOADING_STALL_MS);
    return () => clearTimeout(timer);
  }, [isStillLoading]);

  // Genuine first load — show spinner
  if (isStillLoading && !stalled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Loading stalled — non-destructive prompt
  if (isStillLoading && stalled) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <p className="text-muted-foreground">Loading is taking longer than expected.</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Refresh page
        </button>
      </div>
    );
  }

  // Session expired - redirect to session expired page
  if (!isAuthenticated && sessionExpired) {
    return <Navigate to="/session-expired" state={{ from: location }} replace />;
  }

  // Not authenticated (includes timeout fallback) - redirect to auth.
  // `?redirect=` is what /auth reads to send the user back after signing in;
  // router state alone is ignored there. Same-origin relative paths only.
  if (!isAuthenticated) {
    const target = `${location.pathname}${location.search}`;
    const canReturn = target.startsWith('/') && !target.startsWith('//');
    const to = canReturn ? `/auth?tab=login&redirect=${encodeURIComponent(target)}` : '/auth';
    return <Navigate to={to} state={{ from: location }} replace />;
  }

  // Name comes before everything else (including settings/billing); admins exempt.
  if (!hasRole('admin') && needsName(profile)) {
    const target = `${location.pathname}${location.search}`;
    return <Navigate to={`/onboarding/name?redirect=${encodeURIComponent(target)}`} replace />;
  }

  // Admins have no plan requirement, and routes opting out of the plan gate
  // (settings, billing) skip the onboarding redirects entirely.
  const skipOnboardingGates = hasRole('admin') || !requirePlan;


  // No plan chosen, or the plan lapsed — send them to plan selection.
  if (!skipOnboardingGates && needsPlanSelection(onboardingProgress, subscription)) {
    return <Navigate to="/onboarding/plan" replace />;
  }


  // Business plan but team invite not completed - redirect to team invite
  if (
    !skipOnboardingGates &&
    subscription?.plan === 'business' &&
    !onboardingProgress?.team_invite_completed &&
    !onboardingProgress?.onboarding_completed
  ) {
    return <Navigate to="/onboarding/team" replace />;
  }

  // All checks passed - render children
  return <>{children}</>;
}
